import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { supabase } from "../../../supabaseClient";
import { useAuth } from "../../../auth/AuthContext";

/**
 * Candidates — people who have applied, from submission to enrollment.
 *
 * Trials deliberately live on /trials rather than here: a trial is waiting on
 * the family to decide, an application is waiting on us, and merging the two
 * queues makes neither readable.
 *
 * `submitted` sorts above `awaiting_student`: submitted rows
 * are the queue, awaiting_student rows are a student still writing essays and
 * need nothing from a reviewer. Sorting purely by date buries the former under
 * the latter, which is the wrong way round for the person reading this page.
 *
 * ── What the row actually is ──────────────────────────────────────────────
 * research_application holds student_id and the guardian's details, and
 * nothing else about the student: student_name / school / interest were
 * dropped when the flow became student-first. Name, grade and school come from
 * student_profile + users. Fetched separately and merged here rather than with
 * a PostgREST embed, because the three tables sit behind three different RLS
 * policies and an embed that one of them rejects returns a null branch instead
 * of an error — a blank column with nothing in the console.
 *
 * ── Approve → Enroll ──────────────────────────────────────────────────────
 * Approve (approve-application) picks the program, takes the payment
 * deadline, and emails the student and/or parent plus the invoice details to
 * the office. Payment happens outside Scholar. When the family has paid, the
 * office manager clicks Enroll (enroll-applicant): that click enrolls the
 * student and sends the welcome email. Both dialogs preview first and can
 * send a test to the signed-in person; the functions are plain-fetched so
 * their refusal messages reach the screen.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";

type AppStatus =
  | "awaiting_student"
  | "submitted"
  | "reading"
  | "contacted"
  | "approved"
  /* Not this cohort, but not a no: carried forward as a One-on-One candidate. */
  | "deferred"
  | "enrolled"
  | "declined"
  | "withdrawn";

/* The columns as stored; AppRow is this plus the fields joined in below. */
type AppRecord = {
  id: string;
  student_id: string;
  status: AppStatus;
  guardian_name: string | null;
  guardian_email: string;
  guardian_phone: string | null;
  why: string | null;
  experience: string | null;
  created_at: string;
  submitted_at: string | null;
  decided_at: string | null;
  decided_by: string | null;
  class_id: string | null;
  enrolled_at: string | null;
  /* What they asked for. Null on rows written before the question existed. */
  program_choice: "cohort" | "one_on_one" | null;
  /* How they found us, in their own words. Null for every application before
     2026-09-17, when the question was added. */
  heard_about: string | null;
  heard_about_detail: string | null;
};

const PROGRAM_LABEL: Record<string, string> = {
  cohort: "Cohort",
  one_on_one: "One-on-One",
};

interface AppRow extends AppRecord {
  /* joined */
  student_name: string;
  student_first: string;
  student_email: string;
  grade: number | null;
  school: string | null;
  /* Where the school is. Null on rows written before the question existed —
     which is every applicant before 2026-09-17, including the two from
     outside the US that nothing flagged. */
  school_city: string | null;
  school_state: string | null;
  school_country: string | null;
}

interface ClassOpt {
  class_id: string;
  name: string;
  status: string;
  program_label: string | null;
}

interface PreviewEmail {
  key: string;
  label: string;
  to: string;
  cc: string[];
  subject: string;
  text: string;
}

/* submitted is the queue; awaiting_student is not. Approved rows still need
   an action (Enroll), so they sit above everything already finished. */
const STATUS_RANK: Record<string, number> = {
  submitted: 0,
  reading: 1,
  contacted: 2,
  approved: 3,
  /* Deferred still wants something from us — a One-on-One conversation — so it
     sits with the live work, not down with the finished rows. */
  deferred: 4,
  awaiting_student: 5,
  enrolled: 6,
  declined: 7,
  withdrawn: 8,
};

const STATUS_COLOR: Record<
  string,
  "default" | "primary" | "success" | "warning" | "error" | "info" | "secondary"
> = {
  submitted: "primary",
  reading: "primary",
  contacted: "warning",
  awaiting_student: "default",
  approved: "info",
  deferred: "secondary",
  enrolled: "success",
  declined: "error",
  withdrawn: "default",
};

const STATUS_LABEL: Record<string, string> = {
  awaiting_student: "Writing essays",
  submitted: "Submitted",
  reading: "Reading",
  contacted: "Contacted",
  approved: "Approved",
  deferred: "Deferred",
  enrolled: "Enrolled",
  declined: "Declined",
  withdrawn: "Withdrawn",
};

/* The four things that actually happen to an application that is not joining
   this cohort. They were all recorded as "declined" before, which told whoever
   read the row next something untrue about three of them. */
type Outcome = "decline" | "defer" | "rewrite" | "close" | "remove";

const OUTCOMES: { key: Outcome; label: string; detail: string; cta: string }[] = [
  {
    key: "decline",
    label: "Decline — we reviewed it, and the answer is no",
    detail: "A judgment about the application. Recorded against your name.",
    cta: "Decline",
  },
  {
    key: "defer",
    label: "Defer — not this cohort; move them to One-on-One",
    detail:
      "No judgment: the calendar ran out, or a cohort does not suit them. Marks them as a One-on-One candidate, and sends the family the letter below.",
    cta: "Defer & send the letter",
  },
  {
    key: "rewrite",
    label: "Request an essay rewrite — there is not enough here to judge",
    detail:
      "Not a decision. Sends the letter below, reopens their essays to edit, and leaves them in the open queue as still writing.",
    cta: "Request a rewrite & send the letter",
  },
  {
    key: "close",
    label: "Close — not proceeding this cycle",
    detail:
      "They withdrew, went quiet, or the moment passed. Leaves the queue without saying we said no.",
    cta: "Close",
  },
  {
    key: "remove",
    label: "Remove — this application should never have existed",
    detail:
      "A duplicate, a test, or a student already in the program. Deletes the row; their account and project are untouched.",
    cta: "Remove permanently",
  },
];

/* The questions verbatim, as the student actually answered them on the form
   (the source of truth is QUESTIONS in ardent-research data/terms.ts — these
   two strings must stay in step with it). They were paraphrased here, so staff
   read a summary of a question while the student had answered another. */
const APPLICATION_QUESTIONS = {
  why: "What are your research interests?",
  experience:
    "What have you done to illustrate scientific attitude, curiosity, inventiveness and initiative?",
};

const FILTERS = {
  open: "Open",
  approved: "Approved",
  deferred: "Deferred",
  enrolled: "Enrolled",
  declined: "Declined",
  all: "All",
} as const;
type Filter = keyof typeof FILTERS;

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fullName(u?: {
  first_name?: string | null;
  last_name?: string | null;
}) {
  return `${u?.first_name ?? ""} ${u?.last_name ?? ""}`.trim();
}

async function callFn(
  name: string,
  token: string,
  body: Record<string, unknown>,
): Promise<any> {
  /* Ask for the session at call time rather than trusting the one this page
     rendered with: a dialog left open past the hour-long token lifetime would
     otherwise send an expired token, and the functions gateway rejects it
     with a bare 401 before our function can explain anything.
     getSession() refreshes an expired token first. */
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const bearer = session?.access_token ?? token;
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    if (res.status === 401 && !data?.error)
      throw new Error(
        "Your sign-in has expired. Refresh the page (sign in again if asked) and try once more.",
      );
    throw new Error(
      data?.error ??
        data?.message ??
        data?.msg ??
        `Request failed (${res.status})`,
    );
  }
  return data;
}

/* "Approve and send to student and parent" / "… to parent" / "… without emailing the family" */
function recipientsPhrase(toStudent: boolean, toParent: boolean) {
  if (toStudent && toParent) return "student and parent";
  if (toStudent) return "student";
  if (toParent) return "parent";
  return "";
}

export default function CandidatesPage() {
  const { session } = useAuth();
  const token = session?.access_token ?? "";

  const [apps, setApps] = useState<AppRow[]>([]);
  const [classes, setClasses] = useState<ClassOpt[]>([]);
  const [filter, setFilter] = useState<Filter>("open");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [declining, setDeclining] = useState<AppRow | null>(null);
  const [outcome, setOutcome] = useState<Outcome>("decline");
  /* Defer is the one outcome that writes to a family, so the dialog shows the
     note before it goes and offers a test copy first. */
  const [deferNote, setDeferNote] = useState<{
    subject: string;
    text: string;
    to: string[];
    already_sent_at: string | null;
    /* Which of the two letters this row gets — decided server-side from the
       essays, shown here so the sender knows which one they are sending. */
    variant: "warm" | "brief" | null;
  } | null>(null);
  const [deferBusy, setDeferBusy] = useState<null | "preview" | "test">(null);
  const [deferTested, setDeferTested] = useState<string | null>(null);
  /* Requesting a rewrite writes to the family too, so it gets the same
     treatment as Defer: the letter on screen before it goes, and a test copy
     to yourself first. */
  const [rewriteNote, setRewriteNote] = useState<{
    subject: string;
    text: string;
    to: string[];
    already_sent_at: string | null;
  } | null>(null);
  const [rewriteBusy, setRewriteBusy] = useState<null | "preview" | "test">(
    null,
  );
  const [rewriteTested, setRewriteTested] = useState<string | null>(null);
  const [approving, setApproving] = useState<AppRow | null>(null);
  const [enrolling, setEnrolling] = useState<AppRow | null>(null);
  const [offering, setOffering] = useState(false);
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);

    /* Surface errors rather than rendering an empty table. An RLS rejection
       comes back as { data: null, error }, and a page that quietly shows "no
       candidates" for a permissions problem is how the mentor dashboard once
       told everyone they were caught up. */
    const appsRes = await supabase
      .from("research_application")
      .select(
        "id, student_id, status, guardian_name, guardian_email, guardian_phone, why, experience, created_at, submitted_at, decided_at, decided_by, class_id, enrolled_at, program_choice, heard_about, heard_about_detail",
      )
      .returns<AppRecord[]>()
      .order("created_at", { ascending: false });

    if (appsRes.error) {
      setError(appsRes.error.message);
      setLoading(false);
      return;
    }

    const classRes = await supabase
      .from("class")
      .select("class_id, name, status, program_label")
      .returns<ClassOpt[]>()
      .order("name");
    if (classRes.error) {
      setError(classRes.error.message);
      setLoading(false);
      return;
    }
    setClasses(classRes.data ?? []);

    const appRows = appsRes.data ?? [];
    const studentIds = Array.from(
      new Set(appRows.map((a) => a.student_id).filter(Boolean)),
    );

    let profileById: Record<string, any> = {};
    let userById: Record<string, any> = {};

    if (studentIds.length > 0) {
      const { data: profiles, error: pErr } = await supabase
        .from("student_profile")
        .select(
          "student_id, user_id, grade, school_name, school_city, school_state, school_country",
        )
        .in("student_id", studentIds);
      if (pErr) {
        setError(pErr.message);
        setLoading(false);
        return;
      }
      profileById = Object.fromEntries(
        (profiles ?? []).map((p) => [p.student_id, p]),
      );

      const userIds = Array.from(
        new Set((profiles ?? []).map((p) => p.user_id).filter(Boolean)),
      );
      if (userIds.length > 0) {
        const { data: users, error: uErr } = await supabase
          .from("users")
          .select("user_id, email, first_name, last_name")
          .in("user_id", userIds);
        if (uErr) {
          setError(uErr.message);
          setLoading(false);
          return;
        }
        userById = Object.fromEntries((users ?? []).map((u) => [u.user_id, u]));
      }
    }

    const decorate = (studentId: string) => {
      const p = profileById[studentId];
      const u = p ? userById[p.user_id] : undefined;
      return {
        student_name: fullName(u) || "(no name yet)",
        student_first: (u?.first_name ?? "").trim() || "this student",
        student_email: u?.email ?? "",
        grade: p?.grade ?? null,
        school: p?.school_name ?? null,
        school_city: p?.school_city ?? null,
        school_state: p?.school_state ?? null,
        school_country: p?.school_country ?? null,
      };
    };

    setApps(
      appRows.map((a) => ({ ...a, ...decorate(a.student_id) })) as AppRow[],
    );
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const className = (id: string | null) =>
    classes.find((c) => c.class_id === id)?.name ?? "—";

  const visibleApps = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps
      .filter((a) => {
        if (filter === "open") return !a.decided_at;
        if (filter === "approved") return a.status === "approved";
        if (filter === "deferred") return a.status === "deferred";
        if (filter === "enrolled") return a.status === "enrolled";
        if (filter === "declined")
          return a.status === "declined" || a.status === "withdrawn";
        return true;
      })
      .filter(
        (a) =>
          !q ||
          a.student_name.toLowerCase().includes(q) ||
          a.student_email.toLowerCase().includes(q) ||
          a.guardian_email.toLowerCase().includes(q) ||
          (a.guardian_name ?? "").toLowerCase().includes(q),
      )
      .sort((a, b) => {
        const r = (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
        return r !== 0 ? r : b.created_at.localeCompare(a.created_at);
      });
  }, [apps, filter, search]);

  /* The note Defer will send, fetched when Defer is chosen so the sender can
     read it before committing to it. */
  async function loadDeferNote(row: AppRow) {
    setDeferBusy("preview");
    setDeferNote(null);
    setDeferTested(null);
    try {
      const r = await callFn("send-defer-note", token, {
        application_id: row.id,
        mode: "preview",
      });
      setDeferNote({
        subject: r.subject,
        text: r.text,
        to: r.to ?? [],
        already_sent_at: r.already_sent_at ?? null,
        variant: r.variant ?? null,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeferBusy(null);
    }
  }

  async function testDeferNote() {
    if (!declining) return;
    setDeferBusy("test");
    try {
      const r = await callFn("send-defer-note", token, {
        application_id: declining.id,
        mode: "test",
      });
      setDeferTested(r.sent_to ?? "you");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeferBusy(null);
    }
  }

  /* The rewrite request, fetched when that outcome is chosen so the sender
     reads the letter before committing to it. */
  async function loadRewriteNote(row: AppRow) {
    setRewriteBusy("preview");
    setRewriteNote(null);
    setRewriteTested(null);
    try {
      const r = await callFn("send-rewrite-request", token, {
        application_id: row.id,
        mode: "preview",
      });
      setRewriteNote({
        subject: r.subject,
        text: r.text,
        to: r.to ?? [],
        already_sent_at: r.already_sent_at ?? null,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRewriteBusy(null);
    }
  }

  async function testRewriteNote() {
    if (!declining) return;
    setRewriteBusy("test");
    try {
      const r = await callFn("send-rewrite-request", token, {
        application_id: declining.id,
        mode: "test",
      });
      setRewriteTested(r.sent_to ?? "you");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRewriteBusy(null);
    }
  }

  /* One button carried four different meanings, and recorded all of them as a
     judgment. A cohort that ended is not a rejection, and an application that
     should never have existed is not a decision at all. */
  async function decide() {
    if (!declining) return;
    setActing(true);
    const now = new Date().toISOString();
    const me = session?.user?.id ?? null;

    let err: string | null = null;
    if (outcome === "remove") {
      const { error: e } = await supabase
        .from("research_application")
        .delete()
        .eq("id", declining.id);
      err = e?.message ?? null;
    } else {
      const patch: Record<string, unknown> =
        outcome === "defer"
          ? {
              status: "deferred",
              /* Deferring IS saying they belong in One-on-One, so it records
                 that — marked as ours, not the student's own answer. */
              program_choice: "one_on_one",
              program_choice_source: "staff",
              decided_by: me,
              decided_at: now,
            }
          : outcome === "rewrite"
            ? {
                /* Back to the student to write, and NOT a decision: decided_at
                   and decided_by are cleared so they stay in the open queue
                   rather than dropping into the decided counts.

                   submitted_at must be cleared too. apply-submit refuses a
                   resubmission while it is set ("This application has already
                   been submitted"), so without this the letter would invite a
                   rewrite the form then turns away. */
                status: "awaiting_student",
                submitted_at: null,
                decided_by: null,
                decided_at: null,
              }
            : outcome === "close"
              ? { status: "withdrawn", decided_by: me, decided_at: now }
              : { status: "declined", decided_by: me, decided_at: now };
      const { error: e } = await supabase
        .from("research_application")
        .update(patch)
        .eq("id", declining.id);
      err = e?.message ?? null;
    }

    if (err) {
      setActing(false);
      setError(err);
      return;
    }

    /* Deferring without telling anyone reads to a family exactly like being
       turned down, so the note goes with the decision. The row is already
       deferred by this point: if the mail fails, that is worth saying rather
       than pretending the whole action failed. */
    if (outcome === "defer") {
      try {
        await callFn("send-defer-note", token, {
          application_id: declining.id,
          mode: "send",
        });
      } catch (e) {
        setActing(false);
        setError(
          `Deferred, but the note did not send: ${(e as Error).message} — reopen the dialog to try again.`,
        );
        setDeclining(null);
        setOutcome("decline");
        await load();
        return;
      }
    }

    /* Same reasoning as Defer: the row is already back with the student by
       this point, so a mail failure is worth saying plainly rather than
       pretending the whole action failed. */
    if (outcome === "rewrite") {
      try {
        await callFn("send-rewrite-request", token, {
          application_id: declining.id,
          mode: "send",
        });
      } catch (e) {
        setActing(false);
        setError(
          `Their essays are open again, but the letter did not send: ${(e as Error).message} — reopen the dialog to try again.`,
        );
        setDeclining(null);
        setOutcome("decline");
        await load();
        return;
      }
    }

    setActing(false);
    setDeclining(null);
    setOutcome("decline");
    setDeferNote(null);
    setDeferTested(null);
    setRewriteNote(null);
    setRewriteTested(null);
    await load();
  }

  const openCount = apps.filter((a) => !a.decided_at).length;
  const toEnrollCount = apps.filter((a) => a.status === "approved").length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1240, mx: "auto" }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
            Candidates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {openCount} application{openCount === 1 ? "" : "s"} awaiting a
            decision · {toEnrollCount} approved, waiting to be enrolled.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          disabled={!apps.some((a) => a.status === "awaiting_student")}
          onClick={() => setOffering(true)}
        >
          Email unfinished applicants
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2, whiteSpace: "pre-line" }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, v) => v && setFilter(v)}
        >
          {(Object.keys(FILTERS) as Filter[]).map((f) => (
            <ToggleButton key={f} value={f} sx={{ textTransform: "none" }}>
              {FILTERS[f]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Search name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ p: 6, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {visibleApps.length === 0 ? (
            <Alert severity="info" sx={{ mb: 4 }}>
              No applications match this filter.
            </Alert>
          ) : (
            <Box
              sx={{
                mb: 4,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflowX: "auto",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 44 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Gr</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Wants</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Guardian</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Decision
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleApps.map((a) => (
                    <React.Fragment key={a.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpanded(expanded === a.id ? null : a.id)
                            }
                            aria-label={
                              expanded === a.id ? "Hide essays" : "Show essays"
                            }
                          >
                            {expanded === a.id ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {a.student_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.student_email}
                          </Typography>
                        </TableCell>
                        <TableCell>{a.grade ?? "—"}</TableCell>
                        <TableCell>
                          {a.school ?? "—"}
                          {(() => {
                            /* A school outside the US is the thing to notice
                               before anyone approves it, so it is said in
                               words and coloured, not left to a phone
                               prefix. */
                            const where = [a.school_city, a.school_state]
                              .filter(Boolean)
                              .join(", ");
                            const country = a.school_country;
                            /* The column predates the question: rows written
                               by older paths hold 'US' or 'USA', and some of
                               those say 'USA' over a city that plainly is not
                               (Izmir, Almaty). So a known US spelling is
                               domestic, anything else is worth a look, and
                               nothing here is proof on a legacy row — only
                               the answers given from today forward are. */
                            const US = ["US", "USA", "U.S.", "UNITED STATES"];
                            const outside =
                              !!country &&
                              !US.includes(country.trim().toUpperCase());
                            if (!where && !country) return null;
                            return (
                              <Typography
                                variant="caption"
                                sx={{ display: "block" }}
                                color={outside ? "error.main" : "text.secondary"}
                              >
                                {[where, outside ? country : null]
                                  .filter(Boolean)
                                  .join(" · ")}
                                {outside && " — outside the US"}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {a.program_choice ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={
                                a.program_choice === "one_on_one"
                                  ? "secondary"
                                  : "default"
                              }
                              label={PROGRAM_LABEL[a.program_choice]}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {a.guardian_name ?? "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.guardian_email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={STATUS_LABEL[a.status] ?? a.status}
                            color={STATUS_COLOR[a.status] ?? "default"}
                            variant={
                              a.status === "awaiting_student"
                                ? "outlined"
                                : "filled"
                            }
                          />
                        </TableCell>
                        <TableCell>{fmtDate(a.submitted_at)}</TableCell>
                        <TableCell align="right">
                          {a.status === "enrolled" ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Enrolled in {className(a.class_id)} ·{" "}
                              {fmtDate(a.enrolled_at)}
                            </Typography>
                          ) : a.status === "approved" ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {className(a.class_id)} ·{" "}
                                {fmtDate(a.decided_at)}
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => setEnrolling(a)}
                              >
                                Enroll
                              </Button>
                            </Box>
                          ) : a.decided_at ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {STATUS_LABEL[a.status]} · {fmtDate(a.decided_at)}
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                disabled={a.status === "awaiting_student"}
                                onClick={() => setApproving(a)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => {
                                  setOutcome("decline");
                                  setDeclining(a);
                                }}
                              >
                                Not joining…
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          sx={{
                            py: 0,
                            borderBottom:
                              expanded === a.id ? undefined : "none",
                          }}
                        >
                          <Collapse in={expanded === a.id} unmountOnExit>
                            <Box sx={{ py: 2, px: 1, display: "grid", gap: 2 }}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {APPLICATION_QUESTIONS.why}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                                >
                                  {a.why || "—"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {APPLICATION_QUESTIONS.experience}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                                >
                                  {a.experience || "—"}
                                </Typography>
                              </Box>
                              {a.heard_about && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    How did you hear about us?
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {a.heard_about}
                                    {a.heard_about_detail
                                      ? ` — ${a.heard_about_detail}`
                                      : ""}
                                  </Typography>
                                </Box>
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Started {fmtDate(a.created_at)}
                                {a.guardian_phone
                                  ? ` · guardian phone ${a.guardian_phone}`
                                  : ""}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}

      {/* ── Four outcomes, because "Decline" was carrying all of them ─────── */}
      <Dialog
        open={!!declining}
        onClose={() => !acting && setDeclining(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {declining?.student_name}, grade {declining?.grade ?? "—"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, mb: 1 }}>
            What is happening with this application? Most of these only record
            it. <strong>Defer</strong> and{" "}
            <strong>Request an essay rewrite</strong> also send the family the
            letter shown below — you will see it, and can send yourself a test
            copy, before anything goes.
          </DialogContentText>
          <RadioGroup
            value={outcome}
            onChange={(e) => {
              const next = e.target.value as Outcome;
              setOutcome(next);
              if (next === "defer" && declining) loadDeferNote(declining);
              if (next === "rewrite" && declining) loadRewriteNote(declining);
            }}
          >
            {OUTCOMES.map((o) => (
              <FormControlLabel
                key={o.key}
                value={o.key}
                disabled={acting}
                sx={{ alignItems: "flex-start", mb: 1 }}
                control={<Radio size="small" sx={{ pt: 0.25 }} />}
                label={
                  <Box sx={{ mt: 0.4 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {o.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.detail}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {outcome === "defer" && (
            <Box sx={{ mt: 1 }}>
              {deferBusy === "preview" && (
                <Typography variant="caption" color="text.secondary">
                  Loading the note…
                </Typography>
              )}
              {deferNote && (
                <>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    This sends the letter below to{" "}
                    <strong>{deferNote.to.join(" and ")}</strong>.
                    {deferNote.variant === "warm" && (
                      <>
                        {" "}
                        They wrote their essays, so this is the{" "}
                        <strong>warmer letter</strong> — no closing deadline.
                      </>
                    )}
                    {deferNote.variant === "brief" && (
                      <>
                        {" "}
                        They wrote no essays, so this is the{" "}
                        <strong>brief letter</strong>, which says we close it
                        after September 30.
                      </>
                    )}
                    {deferNote.already_sent_at && (
                      <>
                        {" "}
                        One was already sent{" "}
                        {fmtDate(deferNote.already_sent_at)} — this would be a
                        second.
                      </>
                    )}
                  </Alert>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
                    >
                      {deferNote.subject}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                    >
                      {deferNote.text}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    <Button
                      size="small"
                      disabled={deferBusy !== null || acting}
                      onClick={testDeferNote}
                    >
                      {deferBusy === "test" ? "Sending…" : "Send test to me"}
                    </Button>
                    {deferTested && (
                      <Typography variant="caption" color="success.main">
                        Test sent to {deferTested}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
          {outcome === "rewrite" && (
            <Box sx={{ mt: 1 }}>
              {rewriteBusy === "preview" && (
                <Typography variant="caption" color="text.secondary">
                  Loading the letter…
                </Typography>
              )}
              {rewriteNote && (
                <>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    This sends the letter below to{" "}
                    <strong>{rewriteNote.to.join(" and ")}</strong>, reopens
                    their essays so they can edit and submit again, and leaves
                    them in the open queue. It is not recorded as a decision.
                    {rewriteNote.already_sent_at && (
                      <>
                        {" "}
                        One was already sent{" "}
                        {fmtDate(rewriteNote.already_sent_at)} — this would be a
                        second.
                      </>
                    )}
                  </Alert>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
                    >
                      {rewriteNote.subject}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
                    >
                      {rewriteNote.text}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={testRewriteNote}
                      disabled={rewriteBusy === "test" || acting}
                    >
                      {rewriteBusy === "test" ? "Sending…" : "Send test to me"}
                    </Button>
                    {rewriteTested && (
                      <Typography variant="caption" color="success.main">
                        Test sent to {rewriteTested}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
          {outcome === "remove" && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This deletes the application permanently. Their account, project
              and any work they have done are untouched.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclining(null)} disabled={acting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={outcome === "remove" ? "error" : "primary"}
            onClick={decide}
            disabled={acting}
          >
            {acting
              ? "Saving…"
              : (OUTCOMES.find((o) => o.key === outcome)?.cta ?? "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {approving && (
        <ApproveDialog
          row={approving}
          classes={classes.filter(
            (c) => c.status === "active" || c.status === "upcoming",
          )}
          token={token}
          onClose={() => setApproving(null)}
          onDone={async (msg) => {
            setApproving(null);
            setNotice(msg);
            await load();
          }}
        />
      )}

      {enrolling && (
        <EnrollDialog
          row={enrolling}
          className={className(enrolling.class_id)}
          token={token}
          onClose={() => setEnrolling(null)}
          onDone={async (msg) => {
            setEnrolling(null);
            setNotice(msg);
            await load();
          }}
        />
      )}

      {offering && (
        <TrialOfferDialog
          token={token}
          onClose={() => setOffering(false)}
          onDone={(msg) => {
            setOffering(false);
            setNotice(msg);
          }}
        />
      )}
    </Box>
  );
}

// ── Approve ─────────────────────────────────────────────────────────────────

function ApproveDialog({
  row,
  classes,
  token,
  onClose,
  onDone,
}: {
  row: AppRow;
  classes: ClassOpt[];
  token: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [classId, setClassId] = useState("");
  const [deadline, setDeadline] = useState("");
  const [toStudent, setToStudent] = useState(true);
  const [toParent, setToParent] = useState(true);
  const [preview, setPreview] = useState<PreviewEmail[] | null>(null);
  const [busy, setBusy] = useState<null | "preview" | "test" | "send">(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const emailsFamily = toStudent || toParent;
  const ready = !!classId && (!emailsFamily || !!deadline);
  const body = {
    application_id: row.id,
    class_id: classId,
    deadline,
    to_student: toStudent,
    to_parent: toParent,
  };
  const changed = () => {
    setPreview(null);
    setInfo(null);
  };

  async function run(mode: "preview" | "test" | "send") {
    setBusy(mode);
    setErr(null);
    setInfo(null);
    try {
      const r = await callFn("approve-application", token, { ...body, mode });
      if (mode === "preview") setPreview(r.emails as PreviewEmail[]);
      if (mode === "test")
        setInfo(
          `Test sent to ${r.sent_to}: ${r.count} email${r.count === 1 ? "" : "s"}, marked [TEST].`,
        );
      if (mode === "send") {
        const failed = (r.failed as string[]).length
          ? `\nNot sent (email problem): ${(r.failed as string[]).join(", ")}.`
          : "";
        onDone(
          `${row.student_name} approved. Sent: ${(r.sent as string[]).join(", ") || "nothing"}.${failed}`,
        );
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const who = recipientsPhrase(toStudent, toParent);
  const actionLabel = who
    ? `Approve and send to ${who}`
    : "Approve without emailing the family";

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Approve {row.student_name}'s application
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            select
            size="small"
            label="Program"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              changed();
            }}
          >
            {classes.map((c) => (
              <MenuItem key={c.class_id} value={c.class_id}>
                {c.name}
                {c.program_label ? ` — ${c.program_label}` : ""}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            type="date"
            label="Payment deadline"
            slotProps={{ inputLabel: { shrink: true } }}
            value={deadline}
            onChange={(e) => {
              setDeadline(e.target.value);
              changed();
            }}
            helperText={
              emailsFamily
                ? "Written into the email to the family. Not tracked in Scholar."
                : "Optional when the family is not emailed."
            }
            sx={{ maxWidth: 260 }}
          />

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Send the acceptance email to
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={toStudent}
                  onChange={(e) => {
                    setToStudent(e.target.checked);
                    changed();
                  }}
                />
              }
              label={`Student (${row.student_email || "no email"})`}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={toParent}
                  onChange={(e) => {
                    setToParent(e.target.checked);
                    changed();
                  }}
                />
              }
              label={`Parent (${row.guardian_email})`}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Invoice details always go to wendy.li@ardentacademy.com, cc
              jolin.zhang@ardentacademy.com.
            </Typography>
          </Box>

          {err && <Alert severity="error">{err}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}
          {preview && <EmailPreview emails={preview} />}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          disabled={!ready || !!busy}
          onClick={() => run("preview")}
        >
          {busy === "preview" ? "Loading…" : "Preview emails"}
        </Button>
        <Button
          variant="outlined"
          disabled={!ready || !!busy}
          onClick={() => run("test")}
        >
          {busy === "test" ? "Sending…" : "Send test to me"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={!!busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!ready || !!busy}
          onClick={() => run("send")}
        >
          {busy === "send" ? "Approving…" : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Enroll ──────────────────────────────────────────────────────────────────

function EnrollDialog({
  row,
  className,
  token,
  onClose,
  onDone,
}: {
  row: AppRow;
  className: string;
  token: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [toStudent, setToStudent] = useState(true);
  const [toParent, setToParent] = useState(true);
  const [preview, setPreview] = useState<PreviewEmail[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState<null | "preview" | "test" | "send">(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const body = {
    application_id: row.id,
    to_student: toStudent,
    to_parent: toParent,
  };
  const changed = () => {
    setPreview(null);
    setInfo(null);
  };

  async function run(mode: "preview" | "test" | "send") {
    setBusy(mode);
    setErr(null);
    setInfo(null);
    try {
      const r = await callFn("enroll-applicant", token, { ...body, mode });
      setWarnings((r.warnings as string[]) ?? []);
      if (mode === "preview") setPreview(r.emails as PreviewEmail[]);
      if (mode === "test")
        setInfo(
          `Test sent to ${r.sent_to}: ${r.count} email${r.count === 1 ? "" : "s"}, marked [TEST].`,
        );
      if (mode === "send") {
        const lines = [
          `${row.student_name} enrolled in ${r.class}. Welcome sent: ${(r.sent as string[]).join(", ") || "none"}.`,
        ];
        if ((r.failed as string[]).length)
          lines.push(
            `Not sent (email problem): ${(r.failed as string[]).join(", ")}.`,
          );
        for (const w of (r.warnings as string[]) ?? []) lines.push(w);
        onDone(lines.join("\n"));
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const who = recipientsPhrase(toStudent, toParent);
  const actionLabel = who
    ? `Enroll and send welcome to ${who}`
    : "Enroll without a welcome email";

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Enroll {row.student_name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          <DialogContentText sx={{ fontSize: 14 }}>
            Enroll once the family has paid. This adds {row.student_first} to{" "}
            <strong>{className}</strong>, creates their project, links their
            parent's account, and sets the temporary password shown in the
            welcome email ({row.student_first} will choose a new one at first
            sign-in).
          </DialogContentText>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Send the welcome email to
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={toStudent}
                  onChange={(e) => {
                    setToStudent(e.target.checked);
                    changed();
                  }}
                />
              }
              label={`Student (${row.student_email || "no email"})`}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={toParent}
                  onChange={(e) => {
                    setToParent(e.target.checked);
                    changed();
                  }}
                />
              }
              label={`Parent (${row.guardian_email})`}
            />
          </Box>

          {warnings.map((w) => (
            <Alert key={w} severity="warning">
              {w}
            </Alert>
          ))}
          {err && <Alert severity="error">{err}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}
          {preview && <EmailPreview emails={preview} />}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          disabled={!who || !!busy}
          onClick={() => run("preview")}
        >
          {busy === "preview" ? "Loading…" : "Preview welcome"}
        </Button>
        <Button
          variant="outlined"
          disabled={!who || !!busy}
          onClick={() => run("test")}
        >
          {busy === "test" ? "Sending…" : "Send test to me"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={!!busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!!busy}
          onClick={() => run("send")}
        >
          {busy === "send" ? "Enrolling…" : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Thank-you + free trial, to unfinished applications ──────────────────────

/* The thank-you email for students who started but did not submit, with a
   "Start my free trial" button (send-trial-offer). The dialog is the
   confirmation: it lists every student and parent, shows the email, and the
   send button names the count the server checks. */
function TrialOfferDialog({
  token,
  onClose,
  onDone,
}: {
  token: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState<null | "load" | "test" | "send">("load");
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    callFn("send-trial-offer", token, { mode: "preview" })
      .then(setData)
      .catch((e) => setErr((e as Error).message))
      .finally(() => setBusy(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(mode: "test" | "send") {
    setBusy(mode);
    setErr(null);
    setInfo(null);
    try {
      const r = await callFn("send-trial-offer", token, {
        mode,
        ...(mode === "send" ? { confirm_count: data?.count } : {}),
      });
      if (mode === "test")
        setInfo(
          `Test sent to ${r.sent_to}, written as if to ${r.as_student}. Its button is live and starts a real trial for that student.`,
        );
      else
        onDone(
          `Thank-you email sent for ${r.sent} of ${r.applicants} unfinished applications.${r.failed ? ` ${r.failed} could not be emailed.` : ""}`,
        );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const count: number = data?.count ?? 0;

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Email unfinished applicants
      </DialogTitle>
      <DialogContent>
        {busy === "load" && (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {data && (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <DialogContentText sx={{ fontSize: 14 }}>
              A thank-you to every application that was started but not
              submitted, to the student and the parent, with a button to start a
              free trial. {count} applications · {data.recipient_count} email
              addresses.
            </DialogContentText>
            {(data.applicants ?? []).map(
              (a: {
                student_id: string;
                name: string;
                student_email: string | null;
                guardian_email: string | null;
                offer_sent_at: string | null;
                activated_at: string | null;
              }) => (
                <Box
                  key={a.student_id}
                  sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {a.name}
                    {a.activated_at && (
                      <Chip
                        size="small"
                        color="success"
                        label="Trial started"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[a.student_email, a.guardian_email]
                      .filter(Boolean)
                      .join(", ")}
                    {a.offer_sent_at
                      ? ` · emailed ${fmtDate(a.offer_sent_at)}`
                      : ""}
                  </Typography>
                </Box>
              ),
            )}
            {data.sample_subject && (
              <EmailPreview
                emails={[
                  {
                    key: "sample",
                    label: "Email, as the first family will see it",
                    to: "each student and parent",
                    cc: [],
                    subject: data.sample_subject,
                    text: data.sample_text,
                  },
                ]}
              />
            )}
          </Box>
        )}
        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {info}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          disabled={!!busy || count === 0}
          onClick={() => run("test")}
        >
          {busy === "test" ? "Sending…" : "Send test to me"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={!!busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!!busy || count === 0}
          onClick={() => run("send")}
        >
          {busy === "send"
            ? "Sending…"
            : `Send to ${count} students and their parents`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EmailPreview({ emails }: { emails: PreviewEmail[] }) {
  if (emails.length === 0) {
    return (
      <Alert severity="info">No emails will be sent with these choices.</Alert>
    );
  }
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {emails.map((e) => (
        <Box
          key={e.key}
          sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
        >
          <Typography variant="caption" color="text.secondary">
            {e.label} · to {e.to}
            {e.cc.length ? ` · cc ${e.cc.join(", ")}` : ""}
          </Typography>
          <Typography sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
            {e.subject}
          </Typography>
          <Typography
            component="pre"
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0 }}
          >
            {e.text}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
